import { useCoreApiClient } from "@api/use-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { FaroText } from "@faro-lotv/flat-ui";
import { Link } from "@mui/material";
import { CREDITS_DISPLAY_NAME } from "@src/constants/credit-constants";
import { CoreAPITypes, SphereDashboardAPITypes } from "@stellar/api-logic";
import { selectedSdbCompanyIdSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { sphereColors } from "@styles/common-colors";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useState } from "react";
import { QuoteSuccessDialog } from "@pages/settings/subscription-settings/credits/quote-success-dialog";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { SettingsTabs } from "@router/route-params";
import { QuoteConfirmationDialog } from "@pages/settings/subscription-settings/credits/quote-confirmation-dialog";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { formatStripeDisplayPrice } from "@utils/stripe-product-utils";

interface Props {
  /** The credit package to request quote for */
  selectedPackage: SphereDashboardAPITypes.StripeProduct | undefined;
}

/** Renders request quote for credit package */
export function RequestCreditQuote({ selectedPackage }: Props): JSX.Element {
  const coreApiClient = useCoreApiClient();
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const companyId = useAppSelector(selectedSdbCompanyIdSelector);
  const { navigateToSettingsPage } = useAppNavigation();

  const [isLoading, setIsLoading] = useState(false);
  const [shouldShowConfirmationDialog, setShouldShowConfirmationDialog] =
    useState(false);
  const [shouldShowSuccessDialog, setShouldShowSuccessDialog] = useState(false);

  /**
   * Handles the request to get a quote for the selected credit package.
   */
  const handleRequestCreditQuote = useCallback(async () => {
    if (!selectedPackage || !companyId) {
      return;
    }

    setIsLoading(true);

    trackEvent({
      name: WorkspaceEvents.requestQuote,
      props: {
        creditsAmount: selectedPackage.tokenAmount,
      },
    });

    try {
      // Create a message for the notification that contains the selected package details
      const customMessage = `Requesting quote for:
          • Package: ${selectedPackage.name}
          • Price: ${formatStripeDisplayPrice(selectedPackage.price.price)}`;

      await coreApiClient.V3.notificationRequest({
        companyId,
        featureName: `Requesting to add ${selectedPackage.tokenAmount} ${CREDITS_DISPLAY_NAME}`,
        featureComment: customMessage,
        notificationType: CoreAPITypes.ENotificationType.serviceRequest,
      });

      setShouldShowConfirmationDialog(false);
      setShouldShowSuccessDialog(true);
    } catch (error) {
      handleErrorWithToast({
        id: `sendQuoteRequest-${Date.now().toString()}`,
        title: "Request failed. Please try again.",
        error,
      });
    }
  }, [
    selectedPackage,
    trackEvent,
    coreApiClient.V3,
    companyId,
    handleErrorWithToast,
  ]);

  function onConfirmationDialogClose(): void {
    setShouldShowSuccessDialog(false);

    if (companyId) {
      navigateToSettingsPage({
        companyId,
        settingsTab: SettingsTabs.subscription,
      });
    }
  }

  return (
    <>
      <FaroText
        variant="bodyM"
        sx={{
          marginTop: "18px",
          marginX: "auto",
          display: "inline-flex",
          gap: 0.5,
        }}
      >
        You may{" "}
        <Link
          fontWeight="600"
          color={sphereColors.gray800}
          onClick={() => setShouldShowConfirmationDialog(!!selectedPackage)}
          sx={{ cursor: "pointer" }}
        >
          <SphereTooltip
            title="Please select a package to request a quote"
            shouldShowTooltip={!selectedPackage}
          >
            request a quote
          </SphereTooltip>
        </Link>{" "}
        as an alternative.
      </FaroText>

      {selectedPackage && shouldShowConfirmationDialog && (
        <QuoteConfirmationDialog
          selectedPackage={selectedPackage}
          isLoading={isLoading}
          onConfirm={handleRequestCreditQuote}
          onClose={() => setShouldShowConfirmationDialog(false)}
        />
      )}

      {shouldShowSuccessDialog && (
        <QuoteSuccessDialog onClose={onConfirmationDialogClose} />
      )}
    </>
  );
}
